import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useCallback, useEffect } from 'react'
import { isAxiosError } from 'axios'

import { EducationHostedFormModel, EducationHostedFormSteps } from '../EducationHostedForm.types'
import { useNavigateForm } from '../hooks/useNavigateForm'
import { shouldRunEnrolmentCheck } from '../enrolmentCheck'

import { Box, Spinner } from '@percent/lemonade'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'
import { Step } from '@percent/education-hosted-form/ui'
import { useMutation } from '@percent/education-hosted-form/hooks/useMutation'
import { runHostedFormPrechecks } from '@percent/education-hosted-form/api/hostedFormPrecheck/hostedFormPrecheck.actions'
import {
  PrecheckStatus,
  PrecheckType
} from '@percent/education-hosted-form/api/hostedFormPrecheck/hostedFormPrecheck.types'
import { submitAgentVerification } from '@percent/education-hosted-form/api/verifyAgent/verifyAgent.actions'
import {
  AgentVerificationStatus,
  SubmitAgentVerificationFormProps
} from '@percent/education-hosted-form/api/verifyAgent/verifyAgent.types'
import { formatDateOfBirth } from '@percent/education-hosted-form/utils/formatDateOfBirth'
import { ErrorBase } from '@percent/education-hosted-form/errors/ErrorBase'
import { APIErrorResponse } from '@percent/education-hosted-form/types/api.types'
import { FormState } from '@percent/education-hosted-form/context/educationHostedForm/EducationHostedFormContext.types'

const isOrganisationNotSupportedError = (error: ErrorBase): boolean => {
  if (!isAxiosError<APIErrorResponse>(error.cause)) return false

  return error.cause.response?.data?.error?.code === 'external_agent_verification/organisation_not_supported'
}

export function ProcessingPage() {
  const { t } = useTranslation()
  const { progress, partnerConfig, setAgentVerificationId, setFormState } = useEducationHostedForm()
  const { programId } = useParams<{ programId: string }>()
  const { handleNextStep } = useNavigateForm()
  const { getValues } = useFormContext<EducationHostedFormModel>()

  const enrolmentCheckEnabled = shouldRunEnrolmentCheck(partnerConfig?.configuration)
  const domainMatchEnabled = !!partnerConfig?.configuration.prechecks?.includes(PrecheckType.DomainMatch)

  const buildEavRequest = useCallback((): SubmitAgentVerificationFormProps | undefined => {
    const values = getValues()

    if (!programId || !values.organisationId || !values.dateOfBirth) return undefined

    return {
      validationInviteId: programId,
      organisationId: values.organisationId,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      dateOfBirth: formatDateOfBirth(values.dateOfBirth)
    }
  }, [getValues, programId])

  const { mutate: runEav } = useMutation(submitAgentVerification, {
    onError: (error: ErrorBase) => {
      if (isOrganisationNotSupportedError(error)) {
        handleNextStep(EducationHostedFormSteps.DOCUMENT_UPLOAD)
      } else {
        setFormState(FormState.ERROR)
      }
    },
    onSuccess: ({ data }) => {
      setAgentVerificationId(data.id)

      if (data.isEligible && data.checkStatus === AgentVerificationStatus.Success) {
        handleNextStep(EducationHostedFormSteps.TERMS)
      } else {
        handleNextStep(EducationHostedFormSteps.DOCUMENT_UPLOAD)
      }
    }
  })

  const { mutate: runPrechecks } = useMutation(runHostedFormPrechecks, {
    onError: () => handleNextStep(EducationHostedFormSteps.DOCUMENT_UPLOAD),
    onSuccess: ({ data }) => {
      const domainMatch = data.results.find(result => result.type === PrecheckType.DomainMatch)

      if (domainMatch?.status === PrecheckStatus.Success) {
        handleNextStep(EducationHostedFormSteps.TERMS)

        return
      }

      // Domain match missed → fall through to EAV if enabled, else document upload.
      const eavRequest = enrolmentCheckEnabled ? buildEavRequest() : undefined

      if (eavRequest) {
        runEav(eavRequest)

        return
      }

      handleNextStep(EducationHostedFormSteps.DOCUMENT_UPLOAD)
    }
  })

  useEffect(() => {
    // Clear any id from a prior run so a re-entry (e.g. back-nav after an OTP bounce)
    // never threads a stale verification into the submission.
    setAgentVerificationId(undefined)

    const organisationId = getValues('organisationId')

    if (!programId || !organisationId) return

    if (domainMatchEnabled) {
      runPrechecks({
        validationInviteId: programId,
        prechecks: [{ type: PrecheckType.DomainMatch, email: getValues('email'), organisationId }]
      })

      return
    }

    const eavRequest = enrolmentCheckEnabled ? buildEavRequest() : undefined

    if (eavRequest) {
      runEav(eavRequest)
    } else {
      handleNextStep(EducationHostedFormSteps.DOCUMENT_UPLOAD)
    }
  }, [
    programId,
    getValues,
    runPrechecks,
    runEav,
    buildEavRequest,
    domainMatchEnabled,
    enrolmentCheckEnabled,
    setAgentVerificationId,
    handleNextStep
  ])

  return (
    <Step
      title={t('processingPage.processingTitle')}
      description={t('processingPage.processingDescription')}
      progress={progress}
    >
      <Box mt={48}>
        <Spinner size="xxl" />
      </Box>
    </Step>
  )
}
